import { useOnClickOutside } from "@qastanfit/qastan-ui"
import styles from "../styles/components/Navigation.module.scss"
import chevron from "../assets/icons/chevron_right_white.svg";
import boxlogo from "../assets/logo/boxlogo.svg";
import boxlogo_letters from "../assets/logo/boxlogo_letters.svg";
import { useState, useEffect, useRef, MutableRefObject } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../hooks/redux";
import { motion, AnimatePresence } from "framer-motion"
import { deleteAllCookies, deleteCookie } from "../functions/general/cookies";
import useAuth from "src/hooks/useAuth";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuPortal, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { BoxesIcon, ChevronRightIcon, CloudIcon, CornerDownRightIcon, FileIcon, FoldersIcon, GlobeIcon, HomeIcon, LogOutIcon, MenuIcon, MessageCircleIcon, ScanLineIcon, SearchIcon, WorkflowIcon, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "./ui/hover-card";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuList, NavigationMenuTrigger } from "./ui/navigation-menu";
import { RootState } from "src/store";
import { getWindevLang } from "src/lib/lang";
import { CONFIG } from "src/config";
import { Tabs, TabsContent, TabsTrigger } from "./ui/tabs";
import { TabsList } from "@radix-ui/react-tabs";
import { useWindowSize } from "@uidotdev/usehooks";
import { filterDocgroupTypes } from "src/lib/filter";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";

export default function Navigation(){
    const {t, i18n} = useTranslation()
    const {width} = useWindowSize()
    const {auth} = useAuth()
    const location = useLocation()
    const navigate = useNavigate();

    const docgroups:DocgroupObject[] = useAppSelector((state:RootState)=>state.docgroups)
    const doctypes:DoctypeObject[] = useAppSelector((state:RootState)=>state.doctypes)

    const [open_nav, setopen_nav] = useState(false);

    const logout = () => {
        deleteCookie("fit-token")
        deleteCookie("jwt")
        // window.localStorage.clear();
        // window.sessionStorage.clear();
        navigate("/login")
    }

    const handleChangeLanguage = (lang:string) => {
        i18n.changeLanguage(lang)
    }

    function isActive (page:string) {
        switch (page) {
            case 'home':
                return (
                    !location.pathname.includes('zoeken') && 
                    !location.pathname.includes('dossiers') && 
                    !location.pathname.includes('communicatie') && 
                    !location.pathname.includes('cloud') &&
                    !location.pathname.includes('workflows') &&
                    !location.pathname.includes('scanner') &&
                    !location.pathname.includes('artikels')
                )
            case 'zoeken':
                return location.pathname.includes('zoeken')
            case 'dossiers':
                return location.pathname.includes('dossiers') 
            case 'communicatie':
                return location.pathname.includes('communicatie')
            case 'cloud':
                return location.pathname.includes('cloud')
            case 'workflows':
                return location.pathname.includes('workflows')
            case 'scanner':
                return location.pathname.includes('scanner')
            case 'artikels':
                return location.pathname.includes('artikels')
            default:
                break;
        }
    }

    function doctypeToName (doctype:string) {
        if(!doctypes || !Array.isArray(doctypes)){ return doctype }
        const d = doctypes.find(i=>i.doctype===doctype)
        if(!d){ return doctype }
        return d.docoms?.find(i=>i.nationid===getWindevLang(i18n.language))?.oms || doctype
    }

    const activeIndicator = 'after:content-[""] after:absolute after:w-1 after:h-4 after:bg-primary after:rounded-full after:-left-3 after:top-1/2 after:-translate-y-1/2'
    const activeIndicatorHover = `${activeIndicator} after:opacity-0 hover:after:opacity-30`


    const SearchButtonLink = () => {
        return (
        <>
        {(!width || width >= 768) ?
        <HoverCard openDelay={0} closeDelay={100}>
            <HoverCardTrigger asChild>
                <Link to="/zoeken" className={`relative p-2 rounded-md mx-4 flex items-center justify-between hover:bg-neutral-100 ${activeIndicatorHover} ${isActive('zoeken') ? `bg-neutral-100 ${activeIndicator} after:!opacity-100` : ''}`}>
                    <div className={`flex items-center gap-2 ${isActive('zoeken') ? 'text-neutral-700' : 'text-neutral-500'}`}>
                        <SearchIcon className={`w-4 min-w-4 h-4 min-h-4 ${isActive('zoeken') ? 'text-primary' : 'text-inherit'} `} strokeWidth={1.5}/>
                        <p className={`text-sm text-inherit ${isActive('zoeken') ? 'font-semibold' : 'font-normal'}`}>{t('Zoeken')}</p>
                    </div>
                    <ChevronRightIcon className="w-4 h-4 opacity-50" strokeWidth={1.5}/>
                </Link>
            </HoverCardTrigger>
            <HoverCardContent side={(!width || width > 768) ? "right" : "bottom"} align="start" className="w-full max-w-[calc(100dvw-250px)] max-h-dvh overflow-auto p-0">
                <div className="p-2">
                    <div className="flex flex-col w-full">
                        {docgroups && docgroups.map((docgroup,i)=>(
                        <>
                        {filterDocgroupTypes(docgroup.doctypes, doctypes) && 
                        <div key={`docgroups-${i}`} className="w-full">
                            <HoverCard openDelay={100} closeDelay={100}>
                                <HoverCardTrigger asChild>
                                    <object>
                                    <Link to={`/zoeken?dg=${docgroup.docgroupid}`}>
                                        <div className="flex items-center justify-between w-full gap-4 p-2 text-left rounded-md cursor-pointer group h-fit hover:bg-slate-100">
                                            <div>
                                                <p className="text-sm font-medium text-left group-hover:underline">{docgroup.nations.find(i=>i.nationid===getWindevLang(i18n.language))?.oms || 'Onbekend'}</p>
                                                <p className="max-w-md text-xs font-normal text-neutral-400">{filterDocgroupTypes(docgroup.doctypes, doctypes)?.map((doctype, i)=>(`${doctypeToName(doctype.doctype)}${i!==docgroup.doctypes.length-1?',':''} `))}</p>
                                            </div>
                                            <ChevronRightIcon className="w-3 h-3"/>
                                        </div>
                                    </Link>
                                    </object>
                                </HoverCardTrigger>
                                <HoverCardContent side={(!width || width > 768) ? "right" : "bottom"} align={`${i===docgroups.length-1 ? 'end' : 'start'}`} className="p-0 overflow-auto max-h-dvh" onSelect={(e)=>{e.preventDefault()}}>
                                    <ul className="p-2">
                                        {filterDocgroupTypes(docgroup.doctypes, doctypes)?.map((doctype)=>(
                                            <object key={`nav-dt${doctype.doctype}`}>
                                                <Link to={`/zoeken?dt=${doctype.doctype}`} >
                                                    <li  className="p-2 text-sm rounded-md cursor-pointer hover:underline hover:bg-slate-100 whitespace-nowrap">{doctypeToName(doctype.doctype)}</li>
                                                </Link>
                                            </object>
                                        ))}
                                    </ul>
                                </HoverCardContent>
                            </HoverCard>       
                        </div>
                        }
                        </>
                        ))}
                    </div>
                </div>
            </HoverCardContent>
        </HoverCard>
        :
        <div onClick={()=>{navigate('/zoeken'); setopen_nav(false)}} className={`relative p-2 rounded-md mx-4 flex items-center justify-between ${activeIndicatorHover} ${isActive('zoeken') ? `${activeIndicator} after:!opacity-100` : ''}`}>
            <div className={`flex items-center gap-2 ${isActive('zoeken') ? 'text-neutral-700' : 'text-neutral-500'}`}>
                <SearchIcon className={`w-4 min-w-4 h-4 min-h-4 ${isActive('zoeken') ? 'text-primary' : 'text-inherit'} `} strokeWidth={1.5}/>
                <p className={`text-sm text-inherit ${isActive('zoeken') ? 'font-semibold' : 'font-normal'}`}>{t('Zoeken')}</p>
            </div>
        </div>
        }
        </>
        )
    }

    return (
        <>
        <div className={`${open_nav ? 'h-[100dvh] w-full' : 'h-14 border-b border-neutral-200 shadow-md md:shadow-none w-full md:h-full overflow-hidden'} md:w-[250px] duration-200 fixed z-30 md:relative top-0 left-0 bg-white flex flex-col justify-between md:border-r`}>
            <div className="flex flex-col items-center justify-center pt-0 md:pt-4">
                <div className="relative flex items-center justify-center w-full px-4 md:justify-start h-14">
                    <div className="flex items-center gap-2">
                        <img className={`w-8 h-8`} src={boxlogo} alt="Logo Fitdoc"/>
                        <img className="hidden h-5 md:block" src={boxlogo_letters} alt="Logo Fitdoc"/>
                    </div>
                    
                    <button onClick={()=>{setopen_nav(!open_nav)}} className="absolute flex items-center justify-center w-10 h-10 bg-white rounded-sm md:hidden text-slate-600 right-2 top-2">{open_nav ? <X className="w-5 h-5"/> : <MenuIcon className="w-5 h-5"/>}</button>
                </div>

                <div className="flex flex-col w-full gap-1 mt-4">
                    <Link to="/" onClick={()=>{setopen_nav(false)}} className={`relative p-2 rounded-md mx-4 hover:bg-neutral-100 ${activeIndicatorHover} ${isActive('home') ? `bg-neutral-100 ${activeIndicator} after:!opacity-100` : ''}`}>
                        <div className={`flex items-center gap-2 ${isActive('home') ? 'text-neutral-700' : 'text-neutral-500'}`}>
                            <HomeIcon className={`w-4 min-w-4 h-4 min-h-4 ${isActive('home') ? 'text-primary' : 'text-inherit'} `} strokeWidth={1.5}/>
                            <p className={`text-sm text-inherit ${isActive('home') ? 'font-semibold' : 'font-normal'}`}>{t('Home')}</p>
                        </div>
                    </Link>

                    <SearchButtonLink />

                    <Link to="/dossiers" onClick={()=>{setopen_nav(false)}} className={`relative p-2 rounded-md mx-4 hover:bg-neutral-100 ${activeIndicatorHover} ${isActive('dossiers') ? `bg-neutral-100 ${activeIndicator} after:!opacity-100` : ''}`}>
                        <div className={`flex items-center gap-2 ${isActive('dossiers') ? 'text-neutral-700' : 'text-neutral-500'}`}>
                            <FoldersIcon className={`w-4 min-w-4 h-4 min-h-4 ${isActive('dossiers') ? 'text-primary' : 'text-inherit'} `} strokeWidth={1.5}/>
                            <p className={`text-sm text-inherit ${isActive('dossiers') ? 'font-semibold' : 'font-normal'}`}>{t('Dossiers')}</p>
                        </div>
                    </Link>

                    <Link to="/communicatie" onClick={()=>{setopen_nav(false)}} className={`relative p-2 rounded-md mx-4 hover:bg-neutral-100 ${activeIndicatorHover} ${isActive('communicatie') ? `bg-neutral-100 ${activeIndicator} after:!opacity-100` : ''}`}>
                        <div className={`flex items-center gap-2 ${isActive('communicatie') ? 'text-neutral-700' : 'text-neutral-500'}`}>
                            <MessageCircleIcon className={`w-4 min-w-4 h-4 min-h-4 ${isActive('communicatie') ? 'text-primary' : 'text-inherit'} `} strokeWidth={1.5}/>
                            <p className={`text-sm text-inherit ${isActive('communicatie') ? 'font-semibold' : 'font-normal'}`}>{t('Communicatie')}</p>
                        </div>
                    </Link>

                    {CONFIG.workflow.portal.enable &&
                    <Link to="/workflows" onClick={()=>{setopen_nav(false)}} className={`relative p-2 rounded-md mx-4 hover:bg-neutral-100 ${activeIndicatorHover} ${isActive('workflows') ? `bg-neutral-100 ${activeIndicator} after:!opacity-100` : ''}`}>
                        <div className={`flex items-center gap-2 ${isActive('workflows') ? 'text-neutral-700' : 'text-neutral-500'}`}>
                            <WorkflowIcon className={`w-4 min-w-4 h-4 min-h-4 ${isActive('workflows') ? 'text-primary' : 'text-inherit'} `} strokeWidth={1.5}/>
                            <p className={`text-sm text-inherit ${isActive('workflows') ? 'font-semibold' : 'font-normal'}`}>Workflows</p>
                        </div>
                    </Link>
                    }

                    {CONFIG.onedrive.enable && 
                    <Link to="/cloud" onClick={()=>{setopen_nav(false)}} className={`relative p-2 rounded-md mx-4 hover:bg-neutral-100 ${activeIndicatorHover} ${isActive('cloud') ? `bg-neutral-100 ${activeIndicator} after:!opacity-100` : ''}`}>
                        <div className={`flex items-center gap-2 ${isActive('cloud') ? 'text-neutral-700' : 'text-neutral-500'}`}>
                            <CloudIcon className={`w-4 min-w-4 h-4 min-h-4 ${isActive('cloud') ? 'text-primary' : 'text-inherit'} `} strokeWidth={1.5}/>
                            <p className={`text-sm text-inherit ${isActive('cloud') ? 'font-semibold' : 'font-normal'}`}>Cloud opslag</p>
                        </div>
                    </Link>
                    }

                    {(CONFIG.scanner.navigation) && (width && width <= 768) &&
                    <Link to="/scanner" onClick={()=>{setopen_nav(false)}} className={`relative p-2 rounded-md mx-4 hover:bg-neutral-100 ${activeIndicatorHover} ${isActive('scanner') ? `bg-neutral-100 ${activeIndicator} after:!opacity-100` : ''}`}>
                        <div className={`flex items-center gap-2 ${isActive('scanner') ? 'text-neutral-700' : 'text-neutral-500'}`}>
                            <ScanLineIcon className={`w-4 min-w-4 h-4 min-h-4 ${isActive('scanner') ? 'text-primary' : 'text-inherit'} `} strokeWidth={1.5}/>
                            <p className={`text-sm text-inherit ${isActive('scanner') ? 'font-semibold' : 'font-normal'}`}>Scanner</p>
                        </div>
                    </Link>
                    }

                    {(CONFIG.company && CONFIG.company[auth.data?.user.companyname || '']?.view_article_catalogue && auth.data?.user.fitdocusername === 'SUPERVISOR') &&
                    <Link to="/artikels" onClick={()=>{setopen_nav(false)}} className={`relative p-2 rounded-md mx-4 hover:bg-neutral-100 ${activeIndicatorHover} ${isActive('artikels') ? `bg-neutral-100 ${activeIndicator} after:!opacity-100` : ''}`}>
                        <div className={`flex items-center gap-2 ${isActive('artikels') ? 'text-neutral-700' : 'text-neutral-500'}`}>
                            <BoxesIcon className={`w-4 min-w-4 h-4 min-h-4 ${isActive('artikels') ? 'text-primary' : 'text-inherit'} `} strokeWidth={1.5}/>
                            <p className={`text-sm text-inherit ${isActive('artikels') ? 'font-semibold' : 'font-normal'}`}>Artikels</p>
                        </div>
                    </Link>
                    }
                </div>
            </div>
            <div className="p-4">
                <DropdownMenu>
                    <DropdownMenuTrigger className="w-full overflow-hidden">
                        <div className="flex items-center gap-2 p-1 border rounded-full bg-slate-50">
                            <div className="flex items-center justify-center w-6 h-6 font-medium text-white rounded-full select-none aspect-square bg-primary">{(auth.data?.user.name)?.charAt(0)}</div>
                            <p className="text-sm font-medium text-primary">{auth.data?.user.name}</p>
                        </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                        <DropdownMenuGroup>
                            <DropdownMenuSub>
                                <DropdownMenuSubTrigger className="font-medium">
                                    <GlobeIcon className="w-4 h-4 mr-2 text-neutral-600"/>
                                    <p>{t('Taal')}</p>
                                </DropdownMenuSubTrigger>
                                <DropdownMenuPortal>
                                    <DropdownMenuSubContent>
                                        <DropdownMenuItem onClick={()=>{handleChangeLanguage('nl')}}>Nederlands</DropdownMenuItem>
                                        <DropdownMenuItem onClick={()=>{handleChangeLanguage('fr')}}>Français</DropdownMenuItem>
                                        <DropdownMenuItem onClick={()=>{handleChangeLanguage('en')}}>English</DropdownMenuItem>
                                    </DropdownMenuSubContent>
                                </DropdownMenuPortal>
                            </DropdownMenuSub>
                            <DropdownMenuItem onClick={()=>{logout()}} className="font-medium cursor-pointer">
                                <LogOutIcon className="w-4 h-4 mr-2 text-neutral-600"/>
                                {t('Uitloggen')}
                            </DropdownMenuItem>
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
        </>
    )
}